import React, { useState, useRef, useEffect, useCallback } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { UsageEventsTracker } from '../services/UsageEventsTracker';
import './Navbar.css';

const NAV_ITEMS = [
  { to: '/dashboard', label: 'Dashboard', icon: '📊', end: true },
  { to: '/chat-new', label: 'AI Chat', icon: '💬' },
  { to: '/calendar', label: 'Calendar', icon: '📅' },
  { to: '/documents', label: 'Documents', icon: '📁' },
  { to: '/settings', label: 'Settings', icon: '⚙️' },
];

function Navbar() {
  const { user, logout, hasAnyOrgAdminRole } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.roles?.some(r => r.roleName === 'admin');
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const btnRef = useRef(null);
  const fileInputRef = useRef(null);
  const [localAvatar, setLocalAvatar] = useState(null);

  // Primary nav is explicit above; keep center nav deterministic
  const allNav = [...NAV_ITEMS];
  if (hasAnyOrgAdminRole()) {
    allNav.push({ to: '/my-classes', label: 'My Classes', icon: '🏫' });
  }

  const closeMenu = useCallback(() => setMenuOpen(false), []);
  const toggleMenu = () => setMenuOpen(o => !o);

  useEffect(() => {
    const onKey = e => {
      if (e.key === 'Escape') {
        closeMenu();
      }
    };
    const onClick = e => {
      if (menuOpen && menuRef.current && !menuRef.current.contains(e.target) && !btnRef.current.contains(e.target)) {
        closeMenu();
      }
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onClick);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onClick);
    };
  }, [menuOpen, closeMenu]);

  const handleLogout = async () => {
    closeMenu();
    logout();
    navigate('/');
  };

  // avatar upload preview handling (optional feature)
  useEffect(() => {
    return () => {
      if (localAvatar) URL.revokeObjectURL(localAvatar);
    };
  }, [localAvatar]);

  const handleAvatarClick = () => {
    // open file picker
    fileInputRef.current?.click();
  };

  const handleAvatarChange = e => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    const url = URL.createObjectURL(f);
    setLocalAvatar(url);
    // NOTE: for real app you'd upload to server here and save user.avatarUrl
  };

  // derive initials (prefer first + last initial, fallback to first two letters of email)
  const initials = (() => {
    try {
      if (user) {
        const fn = user.firstName || '';
        const ln = user.lastName || '';
        if (fn || ln) return ((fn[0] || '') + (ln[0] || '')).toUpperCase();
        if (user.email) return user.email.replace(/@.*/, '').slice(0, 2).toUpperCase();
      }
    } catch (e) {}
    return 'U';
  })();

  // show notification dot when user has unread notifications
  const hasNotifications = !!(
    user && ((user.unreadCount && user.unreadCount > 0) || user.hasUnreadNotifications)
  );

  return (
    <header className="app-navbar" role="banner">
      <div className="nav-inner">
        {/* LEFT: Logo */}
        <div className="nav-left" aria-label="Application branding">
          <button
            className="logo-block"
            onClick={() => navigate('/dashboard')}
            aria-label="Go to Dashboard"
          >
            <img
              src="/logos/blue_gold_head.png"
              alt=""
              className="logo-icon-img"
              onError={e => { e.currentTarget.style.display = 'none'; }}
            />
            <img
              src="/logos/blue_docuhounds_text.png"
              alt="DocuHounds"
              className="logo-text-img"
              onError={e => { e.currentTarget.style.display = 'none'; }}
            />
          </button>
        </div>

        {/* CENTER: Navigation Tabs */}
        <nav className="nav-center" aria-label="Primary">
          <ul className="nav-tabs" role="menubar">
            {allNav.map(item => (
              <li key={item.to} role="none">
                <NavLink
                  to={item.to}
                  end={item.end}
                  role="menuitem"
                  onClick={(e) => {
                    // allow modifier keys / non-left clicks to open in new tab/window
                    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
                    e.preventDefault();
                    UsageEventsTracker.recordQueued({ eventType:'NAV_CLICK', payload:{ action:'click', category:'navigation', entityType:'link', entityId:item.to, label:item.label } });
                    closeMenu();
                    navigate(item.to);
                  }}
                  className={({ isActive }) =>
                    'nav-tab' + (isActive ? ' active' : '')
                  }
                >
                  <span className="tab-icon" aria-hidden="true">
                    {item.icon}
                  </span>
                  <span className="tab-label">{item.label}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        {/* RIGHT: User Menu */}
        <div className="nav-right">
          <div className="user-menu">
            <button
              ref={btnRef}
              className="user-btn"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              onClick={toggleMenu}
              aria-label="User menu"
            >
              <div className="avatar-circle" aria-hidden="true">
                {localAvatar || user?.avatarUrl ? (
                  <img src={localAvatar || user.avatarUrl} alt="User avatar" />
                ) : (
                  <span className="avatar-initials">{initials}</span>
                )}
                {hasNotifications && <span className="notification-dot" aria-hidden="true" />}
              </div>
            </button>
            {menuOpen && (
              <div
                ref={menuRef}
                className="user-dropdown"
                role="menu"
                aria-label="User menu"
              >
                <div className="user-meta">
                  <div className="profile-row">
                    <div className="dropdown-avatar"
                      onClick={handleAvatarClick}
                      title="Upload avatar"
                      role="button"
                      tabIndex={0}
                      onKeyDown={e => { if (e.key === 'Enter') handleAvatarClick(); }}
                    >
                        {localAvatar || user?.avatarUrl ? (
                          <img src={localAvatar || user.avatarUrl} alt="User avatar" />
                        ) : (
                          <div className="avatar-initials">{initials}</div>
                        )}
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={handleAvatarChange}
                      />
                    </div>

                    <div className="profile-info">
                      <div className="user-id-line">{user?.email || 'User'}</div>
                      {isAdmin && <span className="admin-chip">Administrator</span>}
                    </div>
                  </div>
                </div>

                <div className="soft-divider" />

                <div className="action-list" role="menu">
                  <button
                    className="action-item"
                    role="menuitem"
                    onClick={() => {
                      closeMenu();
                      navigate('/profile'); // changed from /settings
                    }}
                  >
                    <span className="item-icon" aria-hidden="true">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                        xmlns="http://www.w3.org/2000/svg" aria-hidden>
                        <path d="M12 15.5A3.5 3.5 0 1 0 12 8.5a3.5 3.5 0 0 0 0 7z"
                          stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06A2 2 0 1 1 2.3 17.88l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09c.7 0 1.25-.38 1.51-1a1.65 1.65 0 0 0-.33-1.82L4.3 6.46A2 2 0 1 1 7.13 3.63l.06.06c.46.46 1.09.66 1.71.51.6-.14 1.22-.07 1.71.42L12 6.12"
                          stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </span>
                    <span className="item-label">Profile Settings</span>
                  </button>

                  <button
                    className="action-item"
                    role="menuitem"
                    onClick={() => {
                      closeMenu();
                      navigate('/subscription');
                    }}
                  >
                    <span className="item-icon" aria-hidden="true">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M20 12V8H6a2 2 0 0 1-2-2c0-1.1.9-2 2-2h12v4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M4 6v12c0 1.1.9 2 2 2h14v-8H6a2 2 0 0 1-2-2z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                        <circle cx="15" cy="15" r="1" fill="currentColor"/>
                      </svg>
                    </span>
                    <span className="item-label">Subscription</span>
                  </button>

                  <div className="soft-divider" />

                  <button
                    className="action-item logout"
                    role="menuitem"
                    onClick={handleLogout}
                  >
                    <span className="item-icon" aria-hidden="true">
                      {/* consistent logout line icon */}
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                        <path d="M16 17l5-5-5-5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M21 12H9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M13 19H6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </span>
                    <span className="item-label">Sign Out</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}

export default Navbar;
